import React from "react";
import type { DetectionDebugValues, PaperCameraParams } from "./usePaperCamera";

interface PaperCameraDebugProps {
  detectionScore: number;
  debugValues: DetectionDebugValues;
  lastCapturedImage: string | null;
  params: PaperCameraParams;
  setParams: React.Dispatch<React.SetStateAction<PaperCameraParams>>;
  edgeCanvasRef: React.RefObject<HTMLCanvasElement>;
  onClose?: () => void;
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  fmt,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  fmt?: (v: number) => string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <span
          style={{
            fontSize: 10,
            color: "rgba(255,255,255,0.4)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: 13,
            color: "white",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmt ? fmt(value) : value}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{
          width: "100%",
          accentColor: "rgb(74,222,128)",
          cursor: "pointer",
          margin: 0,
          height: 18,
        }}
      />
    </div>
  );
}

function LegendSwatch({
  color,
  label,
  dashed,
  solid,
}: {
  color: string;
  label: string;
  dashed?: boolean;
  solid?: boolean;
}) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span
        style={{
          display: "inline-block",
          width: 12,
          height: 8,
          borderRadius: 2,
          background: solid ? color : "transparent",
          border: dashed
            ? `1px dashed ${color}`
            : solid
              ? "none"
              : `1px solid ${color}`,
        }}
      />
      {label}
    </span>
  );
}

function Tile({
  label,
  value,
  threshold,
  fmt,
}: {
  label: string;
  value: number;
  threshold: number | null;
  fmt: (v: number) => string;
}) {
  const passing = threshold === null ? value > 0 : value >= threshold;
  return (
    <div
      style={{
        background: passing ? "rgba(74,222,128,0.1)" : "rgba(255,255,255,0.05)",
        border: `1px solid ${passing ? "rgba(74,222,128,0.3)" : "rgba(255,255,255,0.08)"}`,
        borderRadius: 5,
        padding: "5px 8px",
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: "rgba(255,255,255,0.35)",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 15,
          color: passing ? "rgb(74,222,128)" : "rgba(255,255,255,0.55)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {fmt(value)}
      </div>
      {threshold !== null && (
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.22)" }}>
          ≥{threshold}
        </div>
      )}
    </div>
  );
}

export default function PaperCameraDebug({
  detectionScore,
  debugValues,
  lastCapturedImage,
  params,
  setParams,
  edgeCanvasRef,
  onClose,
}: PaperCameraDebugProps) {
  const {
    colorTolerance,
    stripAcceptRatio,
    guideW,
    guideH,
    minExpansion,
    guideCoverMargin,
    minFillRatio,
    detectionHoldMs,
    captureBboxPadding,
    safetyTimerEnabled,
  } = params;
  const { frameFill, bboxFill, edgeContrast } = debugValues;
  const isDetected = detectionScore > 0;
  const scorePct = Math.min(100, detectionScore * 100);

  return (
    <div
      style={{
        width: "100%",
        background: "rgba(7, 7, 23, 0.95)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 14,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        fontFamily: "monospace",
        userSelect: "none",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "rgba(255,255,255,0.7)",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            flexShrink: 0,
          }}
        >
          Paper Detector
        </span>
        <span
          style={{
            fontSize: 10,
            padding: "2px 7px",
            borderRadius: 99,
            marginLeft: "auto",
            background: isDetected
              ? "rgba(74,222,128,0.2)"
              : "rgba(255,255,255,0.07)",
            color: isDetected ? "rgb(74,222,128)" : "rgba(255,255,255,0.35)",
            border: `1px solid ${isDetected ? "rgba(74,222,128,0.45)" : "transparent"}`,
          }}
        >
          {isDetected ? "DETECTED" : "scanning"}
        </span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Hide debug panel"
            style={{
              width: 22,
              height: 22,
              padding: 0,
              borderRadius: 4,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.05)",
              color: "rgba(255,255,255,0.6)",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 12,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* Score bar */}
      <div
        style={{
          position: "relative",
          height: 14,
          borderRadius: 4,
          background: "rgba(255,255,255,0.07)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            width: `${scorePct}%`,
            background: isDetected
              ? "rgb(74,222,128)"
              : "rgba(100,160,255,0.65)",
            transition: "width 0.1s, background 0.2s",
          }}
        />
      </div>

      {/* Preview canvas — edge map */}
      <div
        style={{
          borderRadius: 8,
          overflow: "hidden",
          background: "#000",
          aspectRatio: "4/3",
        }}
      >
        <canvas
          ref={edgeCanvasRef}
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            imageRendering: "pixelated",
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "4px 10px",
          fontSize: 9,
          color: "rgba(255,255,255,0.55)",
          letterSpacing: "0.02em",
        }}
      >
        <LegendSwatch color="rgb(80,220,240)" label="trust (seed)" dashed />
        <LegendSwatch color="rgb(74,222,128)" label="guide" dashed />
        <LegendSwatch color="rgb(40,120,70)" label="cover threshold" dashed />
        <LegendSwatch color="rgb(255,160,0)" label="bbox" />
        <LegendSwatch color="rgb(74,222,128)" label="match" solid />
        <LegendSwatch color="rgb(140,50,50)" label="non-match" solid />
      </div>

      {/* 4-tile grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
        <Tile
          label="score"
          value={detectionScore}
          threshold={null}
          fmt={(v) => v.toFixed(3)}
        />
        <Tile
          label="frameFill"
          value={frameFill}
          threshold={minFillRatio}
          fmt={(v) => v.toFixed(3)}
        />
        <Tile
          label="expansion"
          value={bboxFill}
          threshold={minExpansion}
          fmt={(v) => v.toFixed(3)}
        />
        <Tile
          label="contrast"
          value={edgeContrast}
          threshold={null}
          fmt={(v) => Math.round(v).toString()}
        />
      </div>

      {/* Sliders */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "10px 20px",
        }}
      >
        <Slider
          label="colour tol"
          value={colorTolerance}
          min={5}
          max={120}
          step={1}
          onChange={(v) => setParams((p) => ({ ...p, colorTolerance: v }))}
          fmt={(v) => String(Math.round(v))}
        />
        <Slider
          label="strip match"
          value={stripAcceptRatio}
          min={0.3}
          max={0.95}
          step={0.05}
          onChange={(v) => setParams((p) => ({ ...p, stripAcceptRatio: v }))}
          fmt={(v) => v.toFixed(2)}
        />
        <Slider
          label="guide w"
          value={guideW}
          min={0.2}
          max={0.85}
          step={0.05}
          onChange={(v) => setParams((p) => ({ ...p, guideW: v }))}
          fmt={(v) => v.toFixed(2)}
        />
        <Slider
          label="guide h"
          value={guideH}
          min={0.2}
          max={0.85}
          step={0.05}
          onChange={(v) => setParams((p) => ({ ...p, guideH: v }))}
          fmt={(v) => v.toFixed(2)}
        />
        <Slider
          label="cover margin"
          value={guideCoverMargin}
          min={0}
          max={0.4}
          step={0.01}
          onChange={(v) => setParams((p) => ({ ...p, guideCoverMargin: v }))}
          fmt={(v) => `${Math.round(v * 100)}%`}
        />
        <Slider
          label="min fill"
          value={minFillRatio}
          min={0.02}
          max={0.6}
          step={0.01}
          onChange={(v) => setParams((p) => ({ ...p, minFillRatio: v }))}
          fmt={(v) => v.toFixed(2)}
        />
        <Slider
          label="min expansion"
          value={minExpansion}
          min={0.0}
          max={0.6}
          step={0.02}
          onChange={(v) => setParams((p) => ({ ...p, minExpansion: v }))}
          fmt={(v) => v.toFixed(2)}
        />
        <Slider
          label="hold"
          value={detectionHoldMs}
          min={300}
          max={5000}
          step={100}
          onChange={(v) => setParams((p) => ({ ...p, detectionHoldMs: v }))}
          fmt={(v) => `${Math.round(v)}ms`}
        />
        <Slider
          label="capture pad"
          value={captureBboxPadding}
          min={0}
          max={0.3}
          step={0.01}
          onChange={(v) => setParams((p) => ({ ...p, captureBboxPadding: v }))}
          fmt={(v) => `${Math.round(v * 100)}%`}
        />
      </div>

      <label
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "8px 10px",
          borderRadius: 6,
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
          cursor: "pointer",
        }}
      >
        <span
          style={{
            fontSize: 10,
            color: "rgba(255,255,255,0.6)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          5s safety timer
        </span>
        <input
          type="checkbox"
          checked={safetyTimerEnabled}
          onChange={(e) =>
            setParams((p) => ({ ...p, safetyTimerEnabled: e.target.checked }))
          }
          style={{
            width: 14,
            height: 14,
            margin: 0,
            accentColor: "rgb(74,222,128)",
            cursor: "pointer",
          }}
        />
      </label>

      {/* Last captured image */}
      {lastCapturedImage && (
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span
            style={{
              fontSize: 9,
              color: "rgba(255,255,255,0.4)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            last capture
          </span>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 6,
              alignItems: "center",
            }}
          >
            <div
              style={{
                borderRadius: 6,
                overflow: "hidden",
                background: "#000",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            >
              <img
                src={lastCapturedImage}
                alt="Last captured"
                style={{ width: "100%", display: "block" }}
              />
            </div>
            <a
              href={lastCapturedImage}
              download="paper-capture.jpg"
              style={{
                fontSize: 10,
                color: "rgba(100,180,255,0.8)",
                textDecoration: "none",
                writingMode: "vertical-rl",
              }}
            >
              ↓ save
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
